/* Product-only domestic shipping quotes. The merchant confirmed ZIP-independent
 * US rates. A separate Storefront API quote cart leaves the shopper's cart intact.
 * Reference destination is the store's published Alpharetta ZIP, not geolocation.
 */
(() => {
  'use strict';
  if (customElements.get('ts-product-shipping')) return;
  const endpoint = '/api/2026-10/graphql.json';
  const quotes = new Map();
  const createQuery = `mutation ProductShipping($input: CartInput!) {
    cartCreate(input: $input) {
      cart { id lines(first: 2) { nodes { quantity merchandise { ... on ProductVariant { id } } } } }
      userErrors { message } warnings { message }
    }
  }`;
  const rateQuery = `query ProductShippingRates($id: ID!) {
    cart(id: $id) { ... ShippingRates @defer }
  }
  fragment ShippingRates on Cart {
    deliveryGroups(first: 10, withCarrierRates: true) {
      nodes { deliveryOptions { title deliveryMethodType estimatedCost { amount currencyCode } } }
      pageInfo { hasNextPage }
    }
  }`;

  function parseResponse(body, contentType) {
    if (!contentType.includes('multipart/mixed')) return [JSON.parse(body)];
    const match = contentType.match(/boundary=(?:"([^"]+)"|([^;\s]+))/i);
    if (!match) throw new Error('Incomplete shipping response');
    const payloads = body.split('--' + (match[1] || match[2])).flatMap(part => {
      const split = part.search(/\r?\n\r?\n/);
      if (split < 0) return [];
      const json = part.slice(split).trim();
      return json.startsWith('{') ? [JSON.parse(json)] : [];
    });
    if (!payloads.length || payloads.at(-1).hasNext !== false) throw new Error('Incomplete shipping response');
    return payloads;
  }

  async function request(query, variables) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(endpoint, {
        method: 'POST', credentials: 'omit', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'multipart/mixed; deferSpec=20220824, application/json' },
        body: JSON.stringify({ query, variables })
      });
      if (!response.ok) throw new Error('Shipping service unavailable');
      const payloads = parseResponse(await response.text(), response.headers.get('content-type') || '');
      if (payloads.some(p => p.errors?.length || p.incremental?.some(i => i.errors?.length))) {
        throw new Error('Shipping service unavailable');
      }
      return payloads;
    } finally { clearTimeout(timer); }
  }

  function shippingTotal(groups) {
    if (!groups?.nodes?.length || groups.pageInfo?.hasNextPage) throw new Error('No complete shipping rate');
    let amount = 0, currency = '', from = false;
    const services = [];
    for (const group of groups.nodes) {
      const options = (group.deliveryOptions || []).filter(option => {
        const value = option.estimatedCost?.amount;
        const number = Number(value);
        const manual = /quote|contact|arrang|collect|bill\s*(later|separat)|customer.?s? account|tbd|calculat|pay.*later/i.test(option.title || '');
        return option.deliveryMethodType === 'SHIPPING' && value != null && value !== '' &&
          Number.isFinite(number) && number >= 0 && !manual && /^[A-Z]{3}$/.test(option.estimatedCost.currencyCode);
      }).sort((a, b) => Number(a.estimatedCost.amount) - Number(b.estimatedCost.amount));
      if (!options.length) throw new Error('Shipping needs confirmation');
      const selected = options[0];
      if (currency && currency !== selected.estimatedCost.currencyCode) throw new Error('Mixed shipping currencies');
      currency = selected.estimatedCost.currencyCode;
      amount += Number(selected.estimatedCost.amount);
      from ||= options.length > 1;
      services.push(selected.title);
    }
    return { amount, currency, from, service: [...new Set(services)].join(' + ') };
  }

  async function getQuote(variant, quantity) {
    const key = variant + ':' + quantity;
    const cached = quotes.get(key);
    if (cached && Date.now() < cached.expires) return cached.promise;
    const promise = (async () => {
      const merchandiseId = 'gid://shopify/ProductVariant/' + variant;
      const created = await request(createQuery, { input: {
        lines: [{ merchandiseId, quantity }], buyerIdentity: { countryCode: 'US' },
        delivery: { addresses: [{ selected: true, oneTimeUse: true,
          address: { deliveryAddress: { countryCode: 'US', provinceCode: 'GA', zip: '30005' } }
        }] }
      } });
      const result = created[0]?.data?.cartCreate;
      const line = result?.cart?.lines?.nodes;
      if (!result?.cart?.id || result.userErrors?.length || result.warnings?.length ||
          line?.length !== 1 || line[0].quantity !== quantity || line[0].merchandise?.id !== merchandiseId) {
        throw new Error('Unable to quote the selected option and quantity');
      }
      const payloads = await request(rateQuery, { id: result.cart.id });
      let groups;
      for (const payload of payloads) {
        if (payload.data?.cart?.deliveryGroups) groups = payload.data.cart.deliveryGroups;
        for (const incremental of payload.incremental || []) {
          if (incremental.data?.deliveryGroups) groups = incremental.data.deliveryGroups;
        }
      }
      return shippingTotal(groups);
    })();
    const entry = { promise, expires: Date.now() + 300000 };
    quotes.set(key, entry);
    try { return await promise; }
    catch (error) { if (quotes.get(key) === entry) quotes.delete(key); throw error; }
  }

  class ProductShipping extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.revision = 0;
      this.waitingForActivation = false;
      this.output = this.querySelector('[data-shipping-output]');
      this.retry = this.querySelector('[data-shipping-retry]');
      this.querySelector('[data-shipping-fallback]')?.setAttribute('hidden', '');
      this.output.hidden = false;
      this.form = document.getElementById(this.dataset.formId);
      this.region = this.closest('floated-form, .product__info-container') || this.parentElement;
      const changed = event => {
        const target = event.target;
        if (target.closest('floated-form, .product__info-container') !== this.region) return;
        if (target.closest('variant-selects, variant-radios') ||
            (target.form === this.form && (target.name === 'id' || target.name === 'quantity'))) {
          this.schedule();
        }
      };
      this.region.addEventListener('change', changed, { signal: this.abort.signal });
      this.region.addEventListener('input', changed, { signal: this.abort.signal });
      this.retry.addEventListener('click', () => this.schedule(0), { signal: this.abort.signal });
      this.schedule(0);
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.timer);
      this.revision++;
    }

    schedule(delay = 350) {
      if (document.prerendering) {
        if (!this.waitingForActivation) {
          this.waitingForActivation = true;
          document.addEventListener('prerenderingchange', () => {
            this.waitingForActivation = false;
            if (this.isConnected) this.schedule(0);
          }, {once: true, signal: this.abort.signal});
        }
        return;
      }
      clearTimeout(this.timer);
      const revision = ++this.revision;
      this.retry.hidden = true;
      this.output.textContent = 'Checking rate…';
      this.output.removeAttribute('title');
      this.setAttribute('aria-busy', 'true');
      this.timer = setTimeout(() => this.update(revision), delay);
    }

    selectedVariant() {
      const picker = this.region.querySelector('variant-selects, variant-radios');
      const formId = this.form?.querySelector('input[name="id"]')?.value;
      // The picker also signals combinations that have no matching variant.
      if (picker && 'currentVariant' in picker &&
          (!picker.currentVariant || !formId || String(picker.currentVariant.id) === formId)) {
        return picker.currentVariant;
      }
      if (formId && formId !== this.dataset.variant) {
        return picker?.getVariantData?.().find(variant => String(variant.id) === formId);
      }
      return {
        id: this.dataset.variant,
        available: this.dataset.available === 'true',
        requires_shipping: this.dataset.requiresShipping !== 'false'
      };
    }

    async update(revision) {
      const variant = this.selectedVariant();
      const selected = String(variant?.id || '');
      const input = this.form?.elements.namedItem('quantity');
      const quantity = Number(input?.value || 1);
      const finish = text => {
        if (!this.isConnected || revision !== this.revision) return;
        this.output.textContent = text;
        this.setAttribute('aria-busy', 'false');
      };
      if (!/^\d+$/.test(selected)) {
        finish('Select an available option'); return;
      }
      if (!Number.isSafeInteger(quantity) || quantity < 1 || (input && !input.checkValidity())) {
        finish('Select a valid quantity'); return;
      }
      if (!variant.available) { finish('Currently unavailable'); return; }
      if (variant.requires_shipping === false) {
        finish('Not required'); return;
      }
      try {
        const quote = await getQuote(selected, quantity);
        if (!this.isConnected || revision !== this.revision) return;
        // The form can change while a carrier response is in flight.
        if (String(this.selectedVariant()?.id) !== selected ||
            Number(this.form?.elements.namedItem('quantity')?.value || 1) !== quantity) { this.schedule(); return; }
        const amount = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency', currency: quote.currency
        }).format(quote.amount);
        finish((quote.from ? 'From ' : '') + amount);
        this.output.title = `${quote.service} · This product, quantity ${quantity}. Combined orders, discounts, or other services may change the checkout total.`;
      } catch (_) {
        if (!this.isConnected || revision !== this.revision) return;
        finish('Calculated at checkout');
        this.retry.hidden = false;
      }
    }
  }
  customElements.define('ts-product-shipping', ProductShipping);
})();
